import { useTranslation } from "react-i18next";
import { SubmitHandler } from "react-hook-form";
import { GetProjectResponse, ProjectFolderResponse, WorkItemResponse, usePutApiProjectsByProjectIdFoldersAndFolderIdWorkItemsWorkItemIdMutation } from "api/generatedApi";
import { useDialog } from "shared/dialog/use-dialog";
import { ResultDialogType, useMutation } from "utils/hooks/use-mutation";
import { useWorkItemRestrictions } from "shared/user-restrictions/use-workitems-restrictions";
import { useToast } from "shared/toast/hooks/use-toast";
import { FormDataWorkItem } from "../../create/create-work-item-dialog/create-work-item-form-data";
import { EditWorkItemAmountDialog } from "./edit-work-item-amount";
import { Dispatch, SetStateAction } from "react";

interface Prop {
  project: GetProjectResponse;
  folder: ProjectFolderResponse;
  workItem: WorkItemResponse;
  setLastEdited: Dispatch<SetStateAction<string | null>>;
}

export function useEditWorkItemAmount(props: Prop) {
  const { project, folder, workItem, setLastEdited } = props;

  const { t } = useTranslation();
  const { canEditWorkitem } = useWorkItemRestrictions(project);
  const toast = useToast();
  const [openEditWorkItemAmountDialog, closeEditWorkItemAmountDialog] = useDialog(EditWorkItemAmountDialog);
  const [updateWorkItem] = usePutApiProjectsByProjectIdFoldersAndFolderIdWorkItemsWorkItemIdMutation();

  const wrapMutation = useMutation({
    onSuccess: closeEditWorkItemAmountDialog,
    successProps: {
      description: t("content.measurements.edit.success.description"),
    },
    resultDialogType: ResultDialogType.Toast,
  });

  const handleOnSubmit: SubmitHandler<FormDataWorkItem> = (data) => {
    const projectId = project?.id ?? "";
    const folderId = folder?.projectFolderId ?? "";
    const workItemId = workItem.workItemId ?? "";
    const workItemAmount = data.amount && data.amount > 0 ? data.amount : undefined;

    const create = updateWorkItem({
      projectId,
      folderId,
      workItemId,
      updateWorkItemRequest: {
        workItemAmount,
      },
    });
    wrapMutation(create);
  };

  const handleClick = () => {
    if (!canEditWorkitem(folder)) {
      toast.error(t("content.measurements.edit.restrictionError"));
      return;
    }
    setLastEdited(workItem.workItemId ?? null);
    openEditWorkItemAmountDialog({ onSubmit: handleOnSubmit, workItem });
  };

  return handleClick;
}
